import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart3, TrendingUp, Clock, Target, Book, Trophy, RefreshCw } from 'lucide-react';

interface LearningMetric {
  label: string;
  value: number;
  total: number;
  trend: 'up' | 'down' | 'stable';
  color: string;
}

export const LearningAnalyticsDashboard: React.FC = () => {
  const [metrics] = useState<LearningMetric[]>([
    { label: 'Courses Completed', value: 12, total: 20, trend: 'up', color: 'bg-blue-500' },
    { label: 'Study Hours', value: 45, total: 60, trend: 'up', color: 'bg-green-500' },
    { label: 'Skill Assessments', value: 8, total: 10, trend: 'stable', color: 'bg-purple-500' },
    { label: 'Projects Finished', value: 6, total: 8, trend: 'up', color: 'bg-orange-500' }
  ]);

  const [weeklyGoal] = useState({ target: 20, current: 12 });

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'down': return <TrendingUp className="h-4 w-4 text-red-600 rotate-180" />;
      default: return <div className="h-4 w-4 bg-gray-400 rounded-full" />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Learning Analytics Dashboard
          </div>
          <Button variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="skills">Skills</TabsTrigger>
            <TabsTrigger value="goals">Goals</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {metrics.map((metric, index) => (
                <Card key={index}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{metric.label}</span>
                      {getTrendIcon(metric.trend)}
                    </div>
                    <div className="text-2xl font-bold mb-2">
                      {metric.value}/{metric.total}
                    </div>
                    <Progress 
                      value={(metric.value / metric.total) * 100} 
                      className="h-2"
                    />
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Learning Streaks */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Learning Streak</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-orange-600">7</div>
                    <div className="text-sm text-muted-foreground">Days</div>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-muted-foreground mb-2">
                      Keep it up! You're on a 7-day learning streak.
                    </div>
                    <div className="flex gap-1">
                      {Array.from({ length: 7 }, (_, i) => (
                        <div
                          key={i}
                          className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center"
                        >
                          <Trophy className="h-3 w-3 text-orange-600" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="skills" className="space-y-4">
            {['JavaScript', 'React', 'Python', 'SQL'].map((skill, index) => (
              <Card key={index}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="text-lg font-semibold">{skill}</div>
                      <Badge variant="outline">Level {5 + index}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {(index + 1) * 120}/{(index + 2) * 150} XP
                    </div>
                  </div>
                  <Progress 
                    value={((index + 1) * 120) / ((index + 2) * 150) * 100} 
                    className="h-2"
                  />
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="goals" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Weekly Study Goal
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span>Study Hours This Week</span>
                    <span className="font-semibold">
                      {weeklyGoal.current}/{weeklyGoal.target} hours
                    </span>
                  </div>
                  <Progress 
                    value={(weeklyGoal.current / weeklyGoal.target) * 100} 
                    className="h-3"
                  />
                  <div className="text-sm text-muted-foreground">
                    {weeklyGoal.target - weeklyGoal.current} hours remaining to reach your goal
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};